import React from 'react';
import { formatStatusLabel } from './processQueueUtils';

function ProcessQueueFilters({
    searchTerm,
    onSearchTermChange,
    searchField,
    onSearchFieldChange,
    searchFields,
    statusFilter,
    onStatusFilterChange,
    statusOptions,
    patientName,
    onPatientNameChange,
    createdDate,
    onCreatedDateChange,
    reminderType,
    onReminderTypeChange,
    reminderTypeOptions = [],
    sortBy,
    onSortByChange,
    sortFields,
    sortOrder,
    onSortOrderChange,
    onSubmit,
    onClear,
    canClear,
    showSort = true,
    showSearch = true,
    showPatientName = false,
    showCreatedDate = false,
    showReminderType = false,
}) {
    const content = (
        <div className="row g-2 g-md-3 align-items-center process-queue-filters__row">
            {showSearch && (
                <>
                    <div className="col-12 col-md-6 col-lg-auto process-queue-filters__item process-queue-filters__item--search">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text process-queue-filters__label">Search</span>
                            <input
                                id="queue-search-term"
                                type="text"
                                className="form-control process-queue-filters__control"
                                placeholder="Search…"
                                value={searchTerm}
                                onChange={(e) => onSearchTermChange(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => onSearchTermChange('')}
                                    aria-label="Clear search input"
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text process-queue-filters__label">Search in</span>
                            <select
                                id="queue-search-field"
                                className="form-select form-select-sm process-queue-filters__control"
                                value={searchField}
                                onChange={(e) => onSearchFieldChange(e.target.value)}
                            >
                                {searchFields.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </>
            )}

            {showPatientName && (
                <div className="col-12 col-md-6 col-lg-auto process-queue-filters__item process-queue-filters__item--search">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text process-queue-filters__label">Patient</span>
                        <input
                            type="text"
                            className="form-control process-queue-filters__control"
                            placeholder="Search by patient name…"
                            value={patientName || ''}
                            onChange={(e) => onPatientNameChange?.(e.target.value)}
                        />
                        {patientName && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => onPatientNameChange?.('')}
                                aria-label="Clear patient name"
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {showCreatedDate && (
                <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text process-queue-filters__label">Created</span>
                        <input
                            type="date"
                            className="form-control form-control-sm process-queue-filters__control"
                            value={createdDate || ''}
                            onChange={(e) => onCreatedDateChange?.(e.target.value)}
                            aria-label="Created at date"
                        />
                    </div>
                </div>
            )}

            {showReminderType && (
                <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text process-queue-filters__label">Reminder</span>
                        <select
                            className="form-select form-select-sm process-queue-filters__control"
                            value={reminderType || ''}
                            onChange={(e) => onReminderTypeChange?.(e.target.value)}
                            aria-label="Reminder type"
                        >
                            <option value="">All types</option>
                            {reminderTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                <div className="input-group input-group-sm">
                    <span className="input-group-text process-queue-filters__label">Status</span>
                    <select
                        id="queue-status-filter"
                        className="form-select form-select-sm process-queue-filters__control"
                        value={statusFilter}
                        onChange={(e) => onStatusFilterChange(e.target.value)}
                    >
                        <option value="">All statuses</option>
                        {statusOptions.map((s) => (
                            <option key={s} value={s}>
                                {formatStatusLabel(s)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {showSort && (
                <>
                    <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text process-queue-filters__label">Sort by</span>
                            <select
                                id="queue-sort-by"
                                className="form-select form-select-sm process-queue-filters__control"
                                value={sortBy}
                                onChange={(e) => onSortByChange(e.target.value)}
                            >
                                {sortFields.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text process-queue-filters__label">Order</span>
                            <select
                                id="queue-sort-order"
                                className="form-select form-select-sm process-queue-filters__control process-queue-filters__control--narrow"
                                value={sortOrder}
                                onChange={(e) => onSortOrderChange(e.target.value)}
                            >
                                <option value="DESC">Desc</option>
                                <option value="ASC">Asc</option>
                            </select>
                        </div>
                    </div>
                </>
            )}

            <div className="col-12 col-lg-auto process-queue-filters__actions d-flex flex-nowrap gap-2">
                {showSearch && (
                    <button type="submit" className="btn btn-primary btn-sm text-nowrap">
                        <i className="bi bi-search me-1"></i>
                        Search
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm text-nowrap"
                    onClick={onClear}
                    disabled={!canClear}
                    title="Clear filters"
                >
                    <i className="bi bi-x-lg me-1"></i>
                    Clear
                </button>
            </div>
        </div>
    );

    if (!showSearch) {
        return <div className="process-queue-filters mb-4 p-3 bg-light rounded border">{content}</div>;
    }

    return (
        <form onSubmit={onSubmit} className="process-queue-filters mb-4 p-3 bg-light rounded border">
            {content}
        </form>
    );
}

export default ProcessQueueFilters;
